import { create, useModal } from '@ebay/nice-modal-react';
import { ReactComponent as User } from '@shared/components/assets/icons/User.svg';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LayoutTwo } from 'assets/icons/LayoutTwo.svg';
import { ReactComponent as Notification } from 'assets/icons/Notification.svg';
import { ReactComponent as Settings } from 'assets/icons/Settings.svg';
import { ReactComponent as ShieldLock } from 'assets/icons/ShieldLock.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useCookies from 'hooks/useCookies';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import SettingsModal from 'modules/SettingsModal/SettingsModal';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';
import { FeatureFlag, matrixConstants } from 'utils/Constants';
import { isGuestModeEnabled } from 'utils/utils';

import LayoutTab from './LayoutTab/LayoutTab';
import NotificationsTab from './NotificationsTab/NotificationsTab';
import ProfileTab from './ProfileTab/ProfileTab';
import SecuritySettingsTab from './SecuritySettingsTab/SecuritySettingsTab';

enum Tabs {
  Profile,
  Layout,
  EndToEndEncryption,
  Notifications,
  NotificationsGlobal,
}

/**
 * Account settings modal for the user to manage their account
 * Displays the user's profile information and settings
 */
const AccountSettingsModal = create(() => {
  const { t } = useTranslation();
  const { remove } = useModal(AccountSettingsModal);
  const { matrixClient } = useMatrixContext();
  const [mxLoggedIn] = useCookies<boolean>(matrixConstants.MX_LOGGED_IN, false);
  const isCrossSigningEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_CROSS_SIGNING);
  const isMessengerEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_MESSENGER);
  const isGuestUser = isGuestModeEnabled() && getIsFromGuestServer(matrixClient.getUserId() ?? '');

  const tabs = [
    { id: Tabs.Profile, title: t('profile'), icon: User, contentComponent: ProfileTab },
    ...(isMessengerEnabled
      ? [
          {
            id: Tabs.Layout,
            title: t('layout.title'),
            icon: LayoutTwo,
            contentComponent: LayoutTab,
          },
        ]
      : []),
    ...(mxLoggedIn && isCrossSigningEnabled
      ? [
          {
            id: Tabs.EndToEndEncryption,
            title: t('security'),
            icon: ShieldLock,
            contentComponent: SecuritySettingsTab,
          },
        ]
      : []),
    ...(isMessengerEnabled && !isGuestUser
      ? [
          {
            id: Tabs.Notifications,
            title: t('notifications_label'),
            icon: Notification,
            contentComponent: NotificationsTab,
            subTabs: [
              {
                id: Tabs.NotificationsGlobal,
                title: t('notifications.global'),
                icon: Settings,
                contentComponent: NotificationsTab,
              },
            ],
          },
        ]
      : []),
  ];

  return <SettingsModal tabs={tabs} title={t('settings')} onClose={remove} />;
});

export default AccountSettingsModal;
